import { useEffect, useState } from "react";

/** True when the browser reports no network (best-effort; not guaranteed on native WebView). */
export function isBrowserOffline() {
    if (typeof navigator === "undefined") return false;
    return navigator.onLine === false;
}

/**
 * Heuristic for fetch failures caused by connectivity (offline, timeout, DNS).
 * @param {unknown} error
 */
export function isLikelyOfflineError(error) {
    if (isBrowserOffline()) return true;
    if (!error) return false;
    const msg = String(error.message || error).toLowerCase();
    return (
        msg.includes("failed to fetch") ||
        msg.includes("network error") ||
        msg.includes("network request failed") ||
        msg.includes("load failed") ||
        msg.includes("the internet connection appears to be offline") ||
        msg.includes("err_internet_disconnected") ||
        msg.includes("err_network_changed")
    );
}

/** Subscribe to `online` / `offline` for reactive UI. */
export function useOnlineStatus() {
    const [online, setOnline] = useState(() => !isBrowserOffline());

    useEffect(() => {
        if (typeof window === "undefined") return undefined;
        const onOnline = () => setOnline(true);
        const onOffline = () => setOnline(false);
        window.addEventListener("online", onOnline);
        window.addEventListener("offline", onOffline);
        setOnline(!isBrowserOffline());
        return () => {
            window.removeEventListener("online", onOnline);
            window.removeEventListener("offline", onOffline);
        };
    }, []);

    return online;
}
